import React, { useState, useEffect } from "react";

const Table = ({
  headers,
  rows,
  zIndex = 1,
  onFilterChange,
  currentSort,
  currentSortDir,
  onRowClick,
  /** compact = dense cells (default); comfortable = larger padding/text like dashboard tables */
  density = "compact",
}) => {
  const comfortable = density === "comfortable";
  const [sortState, setSortState] = useState({ key: null, direction: null });

  // Sync with external sort state
  useEffect(() => {
    setSortState({
      key: currentSort,
      direction: currentSortDir
    });
  }, [currentSort, currentSortDir]);

  const handleSort = (header, event, specificDirection = null) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!header.needFilter || !onFilterChange) return;

    let newDirection;

    if (specificDirection) {
      // Direct arrow click - set specific direction or clear if already active
      if (sortState.key === header.key && sortState.direction === specificDirection) {
        newDirection = null; // Clear sort if clicking same arrow again
      } else {
        newDirection = specificDirection;
      }
    } else {
      // Header click - cycle through: asc -> desc -> none
      if (sortState.key === header.key) {
        if (sortState.direction === 'asc') {
          newDirection = 'desc';
        } else if (sortState.direction === 'desc') {
          newDirection = null;
        } else {
          newDirection = 'asc';
        }
      } else {
        // New column, start with asc
        newDirection = 'asc';
      }
    }

    const newSortState = {
      key: newDirection ? header.key : null,
      direction: newDirection
    };

    setSortState(newSortState);

    // Call the callback with the required format
    onFilterChange({
      key: header.key,
      filter: newDirection || 'none'
    });
  };

  const getSortIcon = (header) => {
    if (!header.needFilter) return null;

    const isActive = sortState.key === header.key;
    const direction = sortState.direction;

    return (
      <div className="flex flex-col ml-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
        <svg
          className={`w-2.5 h-2.5 ${isActive && direction === 'asc' ? 'text-white font-bold' : 'text-gray-300'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="4"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
        </svg>
        <svg
          className={`w-2.5 h-2.5 -mt-0.5 ${isActive && direction === 'desc' ? 'text-white font-bold' : 'text-gray-300'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="4"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </div>
    );
  };

  const thPad = comfortable ? "px-3 py-3 text-xs" : "px-2 py-2 text-[10px]";
  const tdPad = comfortable ? "px-3 py-2.5 text-sm" : "px-1 py-0.5 text-xs";

  return (
    <div
      className={`relative overflow-x-auto border border-gray-200 bg-white shadow-sm ${
        comfortable ? "rounded-xl" : "rounded-lg"
      }`}
    >
      <div className="inline-block min-w-full align-middle">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary sticky top-0 z-20 shadow-sm">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  scope="col"
                  className={`${thPad} text-left font-bold text-white uppercase tracking-wider group
                    ${header.sticky ? "sticky top-0" : ""} 
                    ${header.needFilter ? "cursor-pointer hover:bg-primary/90 select-none transition-colors duration-150" : ""}`}
                  style={{
                    left: header.sticky ? `${header.left}px` : undefined,
                    zIndex: header.sticky ? zIndex + 20 : zIndex,
                    width: header.width,
                    minWidth: header.width,
                    maxWidth: header.width,
                    backgroundColor: header.sticky ? "var(--color-primary)" : undefined,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  onClick={(e) => header.needFilter && handleSort(header, e)}
                >
                  <div className="flex items-center truncate">
                    <span className="truncate">{header.label}</span>
                    {getSortIcon(header)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {rows && rows.length > 0 ? (
              rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`relative ${row.rowBgColor ? row.rowBgColor : (rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50/80")} transition-colors duration-150 ${onRowClick ? "cursor-pointer hover:bg-gray-100" : "hover:bg-slate-50"}`}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {headers.map((header, colIndex) => (
                    <td
                      key={colIndex}
                      className={`${tdPad} text-gray-800 align-middle ${header.sticky ? "sticky" : ""}`}
                      style={{
                        left: header.sticky ? `${header.left}px` : undefined,
                        zIndex: header.sticky ? zIndex + 5 : undefined,
                      }}
                    >
                      <div
                        className={typeof row[header.key] === 'string' ? "truncate" : ""}
                        style={{ maxWidth: header.width }}
                      >
                        {row[header.key] || (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={headers.length}
                  className="px-6 py-8 text-center text-sm text-gray-500"
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-base font-medium text-gray-600 mb-1">No data available</p>
                    <p className="text-sm text-gray-500">There are no records to display.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
